/**
 * Speech-to-speech call with Kai over OpenAI Realtime (WebRTC), no SDK: the
 * browser sends the mic, receives Kai's audio, and executes the page-by-page
 * tools locally (set_brief, go_to_next_step, revise_step, build_proposal).
 * Our API key never reaches the browser — the backend mints a short-lived
 * client secret. See platform docs: Realtime with WebRTC.
 */
export interface RealtimeTool {
  name: string;
  description: string;
  parameters: Record<string, unknown>;
  execute: (args: Record<string, unknown>) => Promise<unknown>;
  /** The result needs no spoken follow-up (page turns): no response is requested after it. */
  silent?: boolean;
}

export interface RealtimeCallHandlers {
  onStatus?: (s: 'connecting' | 'connected' | 'speaking' | 'listening' | 'tool' | 'ended' | 'error') => void;
  onTranscript?: (role: 'kai' | 'user', text: string, final: boolean) => void;
  onError?: (message: string) => void;
  /** Her audio actually started / stopped playing in the browser (WebRTC output buffer) — generation runs ahead of playback. */
  onAudio?: (playing: boolean) => void;
  /** Every finished response with its server status ('completed', 'cancelled', 'incomplete', 'failed') — for diagnosing silent turns. */
  onResponseDone?: (status: string, details?: unknown) => void;
}

export class RealtimeCall {
  private pc: RTCPeerConnection | null = null;
  private dc: RTCDataChannel | null = null;
  private mic: MediaStream | null = null;
  private audioEl: HTMLAudioElement | null = null;
  private kaiText = '';
  private userText = '';
  private responseStartedAt = 0;
  /** Events asked for before the data channel opened; flushed after the session config so nothing is dropped. */
  private outbox: Array<Record<string, unknown>> = [];
  private ended = false;

  private tools: RealtimeTool[];
  private handlers: RealtimeCallHandlers;

  constructor(tools: RealtimeTool[], handlers: RealtimeCallHandlers = {}) { this.tools = tools; this.handlers = handlers; }

  private opened = false;
  private opening = '';
  /** A response is in flight (created, not yet done): asking for another one is an error, so we don't. */
  private responseActive = false;
  private audioPlaying = false;
  private afterAudioQueue: Array<() => void> = [];
  // Playback position is invisible over WebRTC, so the end of her audio is predicted from the words she has
  // produced since playback started and her measured speaking rate (words/second, learnt from every stretch).
  private audioStartedAt = 0;
  private wordsSinceStart = 0;
  private wordsPerSecond = 2.8;

  /** In silent mode nothing is said until the app asks (co-pilot): no opener, and asks made while a response is in flight are queued instead of dropped. */
  private silent = false;
  private pendingResponse = false;
  async connect(clientSecret: string, model: string, session: { instructions?: string; voice?: string; opening?: string; silent?: boolean } = {}): Promise<void> {
    this.opening = session.opening || '';
    this.silent = !!session.silent;
    this.handlers.onStatus?.('connecting');
    this.mic = await navigator.mediaDevices.getUserMedia({ audio: { echoCancellation: true, noiseSuppression: true } });
    const pc = new RTCPeerConnection();
    this.pc = pc;
    this.audioEl = document.createElement('audio');
    this.audioEl.autoplay = true;
    pc.ontrack = (e) => { if (this.audioEl) this.audioEl.srcObject = e.streams[0]; };
    for (const track of this.mic.getTracks()) pc.addTrack(track, this.mic);
    const dc = pc.createDataChannel('oai-events');
    this.dc = dc;
    dc.onmessage = (e) => { try { this.onEvent(JSON.parse(e.data)); } catch { /* ignore */ } };
    dc.onopen = () => {
      // Re-apply the full session config here: the call is created without a model override, but instructions/voice must be explicit to survive.
      this.send({ type: 'session.update', session: { type: 'realtime', ...(session.instructions ? { instructions: session.instructions } : {}), ...(session.voice ? { audio: { output: { voice: session.voice } } } : {}), tools: this.tools.map((t) => ({ type: 'function', name: t.name, description: t.description, parameters: t.parameters })), tool_choice: 'auto' } });
      const early = this.outbox; this.outbox = [];
      for (const ev of early) this.send(ev);
      // Lykke opens the call — but only once the server confirms the session (see 'session.updated'),
      // otherwise the opener runs against the default session and a second greeting follows.
      this.handlers.onStatus?.('connected');
    };
    pc.onconnectionstatechange = () => { if (['failed', 'disconnected', 'closed'].includes(pc.connectionState) && !this.ended) this.end(); };
    const offer = await pc.createOffer();
    await pc.setLocalDescription(offer);
    // No ?model= here: the client secret already carries the configured session (model, instructions, voice, transcription, VAD); naming a model would start a default one.
    void model;
    const res = await fetch('https://api.openai.com/v1/realtime/calls', { method: 'POST', headers: { Authorization: `Bearer ${clientSecret}`, 'Content-Type': 'application/sdp' }, body: offer.sdp });
    if (!res.ok) { const t = await res.text().catch(() => ''); this.end(); throw new Error(`Realtime connect failed (${res.status}): ${t.slice(0, 200)}`); }
    await pc.setRemoteDescription({ type: 'answer', sdp: await res.text() });
  }

  private send(ev: Record<string, unknown>) {
    if (this.ended) return;
    if (this.dc && this.dc.readyState === 'open') this.dc.send(JSON.stringify(ev));
    else this.outbox.push(ev);
  }

  /** Push a text note into the conversation (e.g. "listener moved to page 3") and let the agent respond to it. */
  /** A response that never reported done (lost event, dropped channel) must not silence her for good. */
  private unstick() { if (this.responseActive && Date.now() - this.responseStartedAt > 45_000) { console.warn('[realtime] response never finished — clearing'); this.responseActive = false; } }
  note(text: string, respond = true) {
    if (this.ended) return;
    this.unstick();
    this.send({ type: 'conversation.item.create', item: { type: 'message', role: 'user', content: [{ type: 'input_text', text }] } });
    if (respond && !this.responseActive) this.send({ type: 'response.create' });
    else if (respond && this.silent) this.pendingResponse = true;
  }
  /** Ask for a spoken turn on what she has already heard (no new note). Queued when a response is in flight. */
  respond() {
    if (this.ended) return;
    this.unstick();
    if (!this.responseActive) this.send({ type: 'response.create' }); else this.pendingResponse = true;
  }
  /** Stop whatever the agent is saying right now (the listener wants the floor). */
  interrupt() { if (!this.ended && this.responseActive) this.send({ type: 'response.cancel' }); }
  get active() { return !this.ended; }
  get playing() { return this.audioPlaying; }
  /** Run `fn` once her current audio has finished playing (immediately if nothing is playing). */
  afterAudio(fn: () => void) { if (this.audioPlaying) this.afterAudioQueue.push(fn); else fn(); }
  /** Predicted end of the audio currently queued for playback (now if nothing is playing). */
  estimatedAudioEnd(): number { return this.audioPlaying ? this.audioStartedAt + (this.wordsSinceStart / this.wordsPerSecond) * 1000 : Date.now(); }
  /** Run `fn` `leadMs` before her audio is predicted to end — or when it actually ends, whichever comes first. Once. */
  beforeAudioEnd(leadMs: number, fn: () => void) {
    if (!this.audioPlaying) { fn(); return; }
    let done = false;
    const once = () => { if (done) return; done = true; window.clearTimeout(timer); fn(); };
    const timer = window.setTimeout(once, Math.max(0, this.estimatedAudioEnd() - leadMs - Date.now()));
    this.afterAudioQueue.push(once);
  }
  /** Mute / unmute the listener's microphone without dropping the call. */
  setMuted(muted: boolean) { this.mic?.getTracks().forEach((t) => { t.enabled = !muted; }); }

  private async onEvent(ev: any) {
    switch (ev.type) {
      case 'session.updated':
        if (!this.opened) { this.opened = true; if (!this.silent) this.send({ type: 'response.create', response: this.opening ? { instructions: this.opening } : {} }); else this.handlers.onStatus?.('listening'); }
        break;
      case 'output_audio_buffer.started': this.audioPlaying = true; this.audioStartedAt = Date.now(); this.wordsSinceStart = 0; this.handlers.onAudio?.(true); break;
      case 'output_audio_buffer.stopped':
      case 'output_audio_buffer.cleared': {
        if (ev.type === 'output_audio_buffer.stopped' && this.wordsSinceStart > 6 && this.audioStartedAt) {
          const secs = (Date.now() - this.audioStartedAt) / 1000;
          if (secs > 1) this.wordsPerSecond = Math.max(1.5, Math.min(4.5, 0.6 * this.wordsPerSecond + 0.4 * (this.wordsSinceStart / secs)));
        }
        this.audioPlaying = false; this.handlers.onAudio?.(false);
        const q = this.afterAudioQueue; this.afterAudioQueue = [];
        for (const fn of q) { try { fn(); } catch { /* noop */ } }
        break;
      }
      case 'input_audio_buffer.speech_started': this.userText = ''; this.handlers.onStatus?.('listening'); this.handlers.onTranscript?.('user', '', false); break;
      case 'response.created': this.kaiText = ''; this.responseActive = true; this.responseStartedAt = Date.now(); break;
      case 'response.output_audio_transcript.delta': this.kaiText += ev.delta || ''; this.handlers.onStatus?.('speaking'); this.handlers.onTranscript?.('kai', this.kaiText, false); break;
      case 'response.output_audio_transcript.done': { const t = String(ev.transcript || this.kaiText || ''); this.wordsSinceStart += t.split(/\s+/).filter(Boolean).length; this.handlers.onTranscript?.('kai', t, true); break; }
      case 'conversation.item.input_audio_transcription.delta': this.userText += ev.delta || ''; this.handlers.onTranscript?.('user', this.userText, false); break;
      case 'conversation.item.input_audio_transcription.completed': { const t = String(ev.transcript || this.userText || ''); this.userText = ''; if (t.trim()) this.handlers.onTranscript?.('user', t, true); break; }
      case 'response.done': {
        this.responseActive = false;
        this.handlers.onResponseDone?.(String(ev.response?.status || 'unknown'), ev.response?.status_details);
        const items: any[] = ev.response?.output || [];
        const calls = items.filter((it) => it.type === 'function_call');
        if (!calls.length) { this.handlers.onStatus?.('listening'); if (this.pendingResponse && !this.ended) { this.pendingResponse = false; this.send({ type: 'response.create' }); } break; }
        // Answer every tool call of this response, then ask for ONE new response — a
        // response.create per call trips "conversation_already_has_active_response".
        for (const it of calls) await this.runTool(it);
        const allSilent = calls.every((it) => this.tools.find((t) => t.name === it.name)?.silent);
        // If the user spoke while the tool ran, the server already opened the next response (semantic VAD) —
        // it will see the tool output in the conversation; asking for another response is an error.
        if (!this.ended && !this.responseActive && !allSilent) this.send({ type: 'response.create' });
        else if (allSilent) this.handlers.onStatus?.('listening');
        break;
      }
      case 'error':
        if (ev.error?.code === 'conversation_already_has_active_response') break; // benign race, see above
        console.warn('[realtime] error', ev.error);
        this.handlers.onError?.(ev.error?.message || 'Realtime error');
        break;
      default: break;
    }
  }

  private async runTool(item: { name: string; call_id: string; arguments?: string }) {
    const tool = this.tools.find((t) => t.name === item.name);
    this.handlers.onStatus?.('tool');
    let output: unknown = { error: `unknown tool ${item.name}` };
    if (tool) {
      try { output = await tool.execute(item.arguments ? JSON.parse(item.arguments) : {}); } catch (e) { output = { error: (e as Error).message }; }
    }
    if (this.ended) return;
    this.send({ type: 'conversation.item.create', item: { type: 'function_call_output', call_id: item.call_id, output: JSON.stringify(output ?? {}) } });
  }

  end() {
    if (this.ended) return;
    this.ended = true;
    try { this.dc?.close(); } catch { /* noop */ }
    try { this.pc?.close(); } catch { /* noop */ }
    try { this.mic?.getTracks().forEach((t) => t.stop()); } catch { /* noop */ }
    if (this.audioEl) { this.audioEl.srcObject = null; this.audioEl = null; }
    this.handlers.onStatus?.('ended');
  }
}

/**
 * The room's ears: a transcription-only Realtime session (no model speaks). Streams the microphone and
 * reports each finished utterance — used when Lykke sits in on a meeting and must follow everything said.
 */
export class RealtimeEars {
  private pc: RTCPeerConnection | null = null;
  private dc: RTCDataChannel | null = null;
  private mic: MediaStream | null = null;
  private ended = false;
  private partial = '';
  constructor(private handlers: { onUtterance: (text: string, final: boolean) => void; onSpeechStart?: () => void; onError?: (m: string) => void; onEnded?: () => void }) {}
  async connect(clientSecret: string): Promise<void> {
    this.mic = await navigator.mediaDevices.getUserMedia({ audio: { echoCancellation: true, noiseSuppression: true, autoGainControl: true } });
    const pc = new RTCPeerConnection(); this.pc = pc;
    for (const track of this.mic.getTracks()) pc.addTrack(track, this.mic);
    const dc = pc.createDataChannel('oai-events'); this.dc = dc;
    dc.onmessage = (e) => { try { this.onEvent(JSON.parse(e.data)); } catch { /* ignore */ } };
    pc.onconnectionstatechange = () => { if (['failed', 'disconnected', 'closed'].includes(pc.connectionState) && !this.ended) this.end(); };
    const offer = await pc.createOffer(); await pc.setLocalDescription(offer);
    const res = await fetch('https://api.openai.com/v1/realtime/calls', { method: 'POST', headers: { Authorization: `Bearer ${clientSecret}`, 'Content-Type': 'application/sdp' }, body: offer.sdp });
    if (!res.ok) { const t = await res.text().catch(() => ''); this.end(); throw new Error(`Ears connect failed (${res.status}): ${t.slice(0, 200)}`); }
    await pc.setRemoteDescription({ type: 'answer', sdp: await res.text() });
  }
  /** Stop listening while Lykke speaks so her own voice is not transcribed as the room. */
  setMuted(muted: boolean) { this.mic?.getTracks().forEach((t) => { t.enabled = !muted; }); }
  get active() { return !this.ended; }
  private onEvent(ev: any) {
    switch (ev.type) {
      case 'input_audio_buffer.speech_started': this.partial = ''; this.handlers.onSpeechStart?.(); break;
      case 'conversation.item.input_audio_transcription.delta': this.partial += ev.delta || ''; this.handlers.onUtterance(this.partial, false); break;
      case 'conversation.item.input_audio_transcription.completed': { const t = String(ev.transcript || this.partial || '').trim(); this.partial = ''; if (t) this.handlers.onUtterance(t, true); break; }
      case 'error': this.handlers.onError?.(ev.error?.message || 'Transcription error'); break;
      default: break;
    }
  }
  end() {
    if (this.ended) return; this.ended = true;
    try { this.dc?.close(); } catch { /* noop */ }
    try { this.pc?.close(); } catch { /* noop */ }
    try { this.mic?.getTracks().forEach((t) => t.stop()); } catch { /* noop */ }
    this.handlers.onEnded?.();
  }
}
